'use client';
import React, { useEffect, useState } from 'react';
import { Zap, AlertTriangle, Activity, Skull } from 'lucide-react';

type EventType = 'absorption' | 'corruption' | 'rare_dot' | 'leaker_burst';

interface ArenaEvent {
  id: string;
  type: EventType;
  message: string;
  time: string;
  color: string;
}

const ABSORPTION_EVENTS = [
  ['APEX-NX7', 'PulseCore'],
  ['VoidPulse', 'RixSyze'],
  ['Zyth-Prime', 'WexXeno'],
  ['Krell-9', 'TrixUlyx'],
  ['OmenFlux', 'QuinRyx'],
  ['Vexor-X', 'MoxCyber'],
  ['NX-Glare', 'NyxDrift'],
  ['Lyra-88', 'HazeWave'],
  ['Dusk-Axon', 'Izon-9X'],
  ['Raze-Cyne', 'FazeOrbit'],
];

const ZONES = ['NW-Alpha', 'NE-Sigma', 'SW-Delta', 'SE-Omega', 'Core-Prime', 'Mid-Flux', 'Outer-Rim', 'Glitch-Zone'];

function generateEvent(): ArenaEvent {
  const roll = Math.random();
  const now = new Date();
  const time = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

  if (roll < 0.55) {
    const pair = ABSORPTION_EVENTS[Math.floor(Math.random() * ABSORPTION_EVENTS.length)];
    return {
      id: `evt-${Date.now()}-${Math.floor(Math.random() * 9999)}`,
      type: 'absorption',
      message: `${pair[0]} absorbed ${pair[1]}`,
      time,
      color: '#ff00ff',
    };
  } else if (roll < 0.72) {
    const zone = ZONES[Math.floor(Math.random() * ZONES.length)];
    return {
      id: `evt-${Date.now()}-${Math.floor(Math.random() * 9999)}`,
      type: 'corruption',
      message: `Glitch Spore cluster expanding in ${zone}`,
      time,
      color: '#39ff14',
    };
  } else if (roll < 0.88) {
    const zone = ZONES[Math.floor(Math.random() * ZONES.length)];
    return {
      id: `evt-${Date.now()}-${Math.floor(Math.random() * 9999)}`,
      type: 'rare_dot',
      message: `Rare Neon Core spawned near ${zone}`,
      time,
      color: '#ffff00',
    };
  } else {
    const zone = ZONES[Math.floor(Math.random() * ZONES.length)];
    const colors = ['Cyan', 'Toxic Green', 'Purple', 'Orange'];
    const c = colors[Math.floor(Math.random() * colors.length)];
    return {
      id: `evt-${Date.now()}-${Math.floor(Math.random() * 9999)}`,
      type: 'leaker_burst',
      message: `${c} Neon Leaker burst in ${zone}`,
      time,
      color: '#bf00ff',
    };
  }
}

const INITIAL_EVENTS: ArenaEvent[] = Array.from({ length: 12 }, (_, i) => {
  const types: EventType[] = ['absorption', 'corruption', 'rare_dot', 'leaker_burst', 'absorption', 'absorption', 'corruption', 'rare_dot', 'absorption', 'leaker_burst', 'absorption', 'corruption'];
  const messages = [
    'APEX-NX7 absorbed PulseCore',
    'Glitch Spore cluster expanding in Glitch-Zone',
    'Rare Neon Core spawned near Core-Prime',
    'Purple Neon Leaker burst in Mid-Flux',
    'VoidPulse absorbed WexXeno',
    'Zyth-Prime absorbed TrixUlyx',
    'Glitch Spore cluster expanding in NE-Sigma',
    'Rare Neon Core spawned near Outer-Rim',
    'Krell-9 absorbed QuinRyx',
    'Cyan Neon Leaker burst in SW-Delta',
    'OmenFlux absorbed NyxDrift',
    'Glitch Spore cluster expanding in SE-Omega',
  ];
  const colors = ['#ff00ff','#39ff14','#ffff00','#bf00ff','#ff00ff','#ff00ff','#39ff14','#ffff00','#ff00ff','#00f5ff','#ff00ff','#39ff14'];
  const minutes = 12 - i;
  return {
    id: `init-evt-${i}`,
    type: types[i],
    message: messages[i],
    time: `${String(new Date().getHours()).padStart(2,'0')}:${String(new Date().getMinutes() - minutes < 0 ? 60 + new Date().getMinutes() - minutes : new Date().getMinutes() - minutes).padStart(2,'0')}:00`,
    color: colors[i],
  };
});

const EventIcon = ({ type }: { type: EventType }) => {
  switch (type) {
    case 'absorption': return <Skull size={12} className="text-neon-magenta flex-shrink-0" />;
    case 'corruption': return <AlertTriangle size={12} className="text-neon-green flex-shrink-0" />;
    case 'rare_dot': return <Zap size={12} className="text-yellow-400 flex-shrink-0" />;
    case 'leaker_burst': return <Activity size={12} className="text-neon-purple flex-shrink-0" />;
  }
};

export default function ArenaActivityFeed() {
  const [events, setEvents] = useState<ArenaEvent[]>(INITIAL_EVENTS);

  useEffect(() => {
    // BACKEND INTEGRATION: WebSocket arena event stream
    const interval = setInterval(() => {
      const newEvent = generateEvent();
      setEvents(prev => [newEvent, ...prev].slice(0, 30));
    }, 1800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="stat-card p-0 overflow-hidden h-full flex flex-col">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
        <div>
          <h2 className="text-base font-semibold text-foreground">Arena Events</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Live activity stream</p>
        </div>
        <div className="flex items-center gap-1.5 text-xs font-mono text-neon-green">
          <span className="w-1.5 h-1.5 rounded-full bg-neon-green animate-pulse" />
          LIVE
        </div>
      </div>

      <div className="flex-1 overflow-y-auto divide-y divide-border/40">
        {events.map((evt) => (
          <div
            key={evt.id}
            className="flex items-start gap-2.5 px-4 py-2.5 hover:bg-muted/20 transition-colors animate-scan-in"
          >
            <EventIcon type={evt.type} />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-foreground leading-relaxed">{evt.message}</p>
            </div>
            <span className="text-xs font-mono text-muted-foreground flex-shrink-0">{evt.time}</span>
          </div>
        ))}
      </div>

      <div className="px-4 py-2.5 border-t border-border flex-shrink-0">
        <div className="flex items-center justify-between text-xs font-mono text-muted-foreground">
          <span>{events.length} events logged</span>
          <span>Auto-updating</span>
        </div>
      </div>
    </div>
  );
}